import { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useCertStore } from './stores/certStore';
import { useEntitlementStore } from './stores/entitlementStore';
import { useCertifications } from './hooks/useCertifications';
import { applyProviderTheme } from './utils/providerTheme';
import { useRouteFocus } from './hooks/useFocusManagement';
import CookieConsentBanner from './components/CookieConsentBanner';
import MaintenanceGate from './components/MaintenanceGate';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './pages/Dashboard';
import StudyControlCenter from './pages/StudyControlCenter';
import StudySession from './pages/StudySession';
// ExamMode is lazy-loaded below
import ExamResults from './pages/ExamResults';
// LandingPage is lazy-loaded below
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import BillingPage from './pages/BillingPage';
import ReviewPage from './pages/ReviewPage';
import FlaggedQuestionsPage from './pages/FlaggedQuestionsPage';
import PrivacyPage from './pages/PrivacyPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import TermsPage from './pages/TermsPage';
import CGVPage from './pages/CGVPage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';

/* -- Lazy-loaded public pages ------------------------------------------------- */
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Pricing = lazy(() => import('./pages/Pricing'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage'));
const ExamMode = lazy(() => import('./pages/ExamMode'));

/* -- Lazy-loaded admin console (standalone surface, code-split) -------------- */
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import('./pages/admin/OverviewPage'));
const AdminActivity = lazy(() => import('./pages/admin/ActivityPage'));
const AdminQuestionBank = lazy(() => import('./pages/admin/QuestionsPage'));
const AdminContent = lazy(() => import('./pages/admin/ContentPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/UsersPage'));
const AdminBillingPage = lazy(() => import('./pages/admin/AdminBillingPage'));
const AdminAccess = lazy(() => import('./pages/admin/AccessPage'));
const AdminSettings = lazy(() => import('./pages/admin/SettingsPage'));

/** Fallback spinner shown while lazy chunks are loading. */
function LazyFallback() {
  return <LoadingSpinner fullScreen />;
}

export default function App() {
  const loadFromStorage = useAuthStore((s) => s.loadFromStorage);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const providerCode = useCertStore((s) => s.providerCode);
  const initFromApi = useCertStore((s) => s.initFromApi);
  const fetchEntitlements = useEntitlementStore((s) => s.fetchEntitlements);
  const resetEntitlements = useEntitlementStore((s) => s.reset);
  const { data: certifications } = useCertifications();

  useEffect(() => {
    void loadFromStorage();
  }, [loadFromStorage]);

  /* Fetch entitlements when authenticated; clear when logged out */
  useEffect(() => {
    if (isAuthenticated) {
      void fetchEntitlements();
    } else {
      resetEntitlements();
    }
  }, [isAuthenticated, fetchEntitlements, resetEntitlements]);

  /* Initialize certStore once certifications are fetched */
  useEffect(() => {
    if (certifications && certifications.length > 0) {
      initFromApi(certifications);
    }
  }, [certifications, initFromApi]);

  /* Apply provider theme whenever the active provider changes */
  useEffect(() => {
    if (providerCode) {
      applyProviderTheme(providerCode);
    }
  }, [providerCode]);

  // Auto-focus main content on route changes
  useRouteFocus();

  return (
    <>
      {/* Skip navigation link — first focusable element */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-white focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Aria-live regions for dynamic announcements */}
      <div id="aria-live-polite" aria-live="polite" aria-atomic="true" className="sr-only" />
      <div id="aria-live-assertive" aria-live="assertive" aria-atomic="true" className="sr-only" />

      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Suspense fallback={<LazyFallback />}><LandingPage /></Suspense>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/auth/callback" element={<Suspense fallback={<LazyFallback />}><AuthCallback /></Suspense>} />
        <Route path="/oauth-consent" element={<Suspense fallback={<LazyFallback />}><OAuthConsentPage /></Suspense>} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><ErrorBoundary section="study"><Dashboard /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/study" element={<ProtectedRoute><ErrorBoundary section="study"><StudyControlCenter /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/study/session/:id" element={<ProtectedRoute><ErrorBoundary section="study"><StudySession /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/exam" element={<ProtectedRoute><ErrorBoundary section="study"><Suspense fallback={<LazyFallback />}><ExamMode /></Suspense></ErrorBoundary></ProtectedRoute>} />
        <Route path="/exam/results/:sessionId" element={<ProtectedRoute><ErrorBoundary section="study"><ExamResults /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ErrorBoundary section="profile"><ProfilePage /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><ErrorBoundary section="billing"><BillingPage /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/review" element={<ProtectedRoute><ErrorBoundary section="study"><ReviewPage /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/bookmarks" element={<ProtectedRoute><ErrorBoundary section="study"><FlaggedQuestionsPage /></ErrorBoundary></ProtectedRoute>} />

        {/* Disabled placeholder destinations (browse, learning-paths, skill-iq, drills,
            bookmarks) — routes removed, sidebar shows them as non-clickable "Soon" rows.
            Direct visits fall through to the catch-all redirect below. */}
        <Route path="/pricing" element={<Suspense fallback={<LazyFallback />}><Pricing /></Suspense>} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cgv" element={<CGVPage />} />
        <Route path="/verify/:hash" element={<VerifyCertificatePage />} />

        {/* Admin routes — lazy loaded and guarded */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <ErrorBoundary section="admin">
                <Suspense fallback={<LazyFallback />}>
                  <AdminLayout />
                </Suspense>
              </ErrorBoundary>
            </AdminRoute>
          }
        >
          <Route index element={<Suspense fallback={<LazyFallback />}><AdminOverview /></Suspense>} />
          <Route path="activity" element={<Suspense fallback={<LazyFallback />}><AdminActivity /></Suspense>} />
          <Route path="questions" element={<Suspense fallback={<LazyFallback />}><AdminQuestionBank /></Suspense>} />
          <Route path="content" element={<Suspense fallback={<LazyFallback />}><AdminContent /></Suspense>} />
          <Route path="users" element={<Suspense fallback={<LazyFallback />}><AdminUsersPage /></Suspense>} />
          <Route path="users/:userId" element={<Suspense fallback={<LazyFallback />}><AdminUsersPage /></Suspense>} />
          <Route path="billing" element={<Suspense fallback={<LazyFallback />}><AdminBillingPage /></Suspense>} />
          <Route path="access" element={<Suspense fallback={<LazyFallback />}><AdminAccess /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<LazyFallback />}><AdminSettings /></Suspense>} />

          {/* Legacy admin routes → consolidated sections (see docs/admin-portal-refactor.md) */}
          <Route path="traffic" element={<Navigate to="/admin/activity?tab=traffic" replace />} />
          <Route path="audit-log" element={<Navigate to="/admin/activity?tab=audit" replace />} />
          <Route path="sessions" element={<Navigate to="/admin/activity?tab=sessions" replace />} />
          <Route path="user-stats" element={<Navigate to="/admin/activity" replace />} />
          <Route path="reports" element={<Navigate to="/admin/questions?tab=reports" replace />} />
          <Route path="analytics" element={<Navigate to="/admin" replace />} />
          <Route path="notifications" element={<Navigate to="/admin/settings" replace />} />
          <Route path="questions/new" element={<Navigate to="/admin/questions" replace />} />
          <Route path="questions/:questionId" element={<Navigate to="/admin/questions" replace />} />
          <Route path="questions/:questionId/edit" element={<Navigate to="/admin/questions" replace />} />
        </Route>

        {/* Catch-all — unknown paths (incl. retired /browse, /drills…) go home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>

      {/* Cookie consent banner — rendered outside Routes, above all content */}
      <CookieConsentBanner />

      {/* Maintenance overlay — activates on cm:maintenance custom event from api/client.ts */}
      <MaintenanceGate />
    </>
  );
}
